import { useEffect, Suspense } from "react";
import { Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
import AppSchoolRedirect from "./AppSchoolRedirect";
import AdminAppRedirect from "./AdminAppRedirect";
import { useAuth } from "../context/AuthContext";
import { useStudentTier } from "../hooks/useStudentTier";
import GuestLayout from "../layouts/Guest/GuestLayout";
import NotFound from "../components/common/NotFound";
import { lazyWithRetry } from "../lib/lazyWithRetry";
import RouteErrorBoundary from "../components/common/RouteErrorBoundary";
import RouteLoadingFallback from "../components/common/RouteLoadingFallback";

// Lazy-loaded portals & routers (Code Splitting)
const AuthRouter = lazyWithRetry(() => import("./AuthRouter"));
const GuestRouter = lazyWithRetry(() => import("./GuestRouter"));
const AdminRoute = lazyWithRetry(() => import("./AdminRoute"));
const SchoolRoute = lazyWithRetry(() => import("./SchoolRoute"));
const KidsRouter = lazyWithRetry(() => import("./KidsRouter"));
const TeenRouter = lazyWithRetry(() => import("./TeenRouter"));
const QuranRouter = lazyWithRetry(() => import("./QuranRouter"));
const FamilyInvitationAccept = lazyWithRetry(() => import("../pages/guest/FamilyInvitationAccept"));
const SchoolInviteLanding = lazyWithRetry(() => import("../pages/app/SchoolInviteLanding"));
const SchoolInviteSuccess = lazyWithRetry(() => import("../pages/app/SchoolInviteSuccess"));
const Onboarding = lazyWithRetry(() => import("../pages/guest/Onboarding"));
const CircuitLessonPage = lazyWithRetry(() => import("../pages/CircuitLessonPage"));

function LazyRoute(props) {
  const Component = props.component;
  return (
    <RouteErrorBoundary>
      <Suspense fallback={<RouteLoadingFallback />}>
        <Component />
      </Suspense>
    </RouteErrorBoundary>
  );
}

function StudentAppRedirect() {
  const { user } = useAuth();
  const { isKids, isTeen } = useStudentTier();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const portalParam = searchParams.get('portal');
  const redirectParam = searchParams.get('redirect');

  const role = (user?.role || user?.roles?.[0])?.toLowerCase();
  const isAdmin = role === 'admin' || role === 'superadmin' || user?.is_superuser;

  const sessionPortal = typeof sessionStorage !== 'undefined' ? sessionStorage.getItem('last_dashboard_portal') : null;
  const lastPortal = portalParam || sessionPortal;
  const isViedma = !isAdmin && (lastPortal === 'viedma' || lastPortal === 'viedma_teen' || lastPortal === 'viedma_kids' || redirectParam?.includes('viedma'));
  const isExplicitQuran = portalParam === 'quran' || redirectParam?.startsWith('/quran');
  const isQuran = !isAdmin && (isExplicitQuran || lastPortal === 'quran');

  // 1. Admin priority: Admins should NEVER be redirected to Quran based on stale storage
  if (isAdmin) {
    if (isExplicitQuran) {
      return <Navigate to={redirectParam?.startsWith('/quran') ? redirectParam : "/quran"} replace />;
    }
    if (redirectParam && redirectParam.startsWith('/') && !redirectParam.startsWith('//') && !redirectParam.startsWith('/login')) {
      return <Navigate to={redirectParam} replace />;
    }
    return <Navigate to="/admin" replace />;
  }

  // 2. Quran portal for students
  if (isQuran) {
    return <Navigate to={redirectParam?.startsWith('/quran') ? redirectParam : "/quran"} replace />;
  }

  // 3. Viedma portal
  if (isViedma) {
    if (isKids) return <Navigate to="/kids/viedma" replace />;
    if (isTeen) return <Navigate to="/teen/viedma" replace />;
    return <Navigate to="/app/viedma" replace />;
  }

  // 4. Redirect param
  if (redirectParam && redirectParam.startsWith('/') && !redirectParam.startsWith('//') && !redirectParam.startsWith('/login')) {
    return <Navigate to={redirectParam} replace />;
  }

  // 5. Onboarding check for social / new users
  if (!user?.birth_date || sessionStorage.getItem('oauth_onboarding_pending') === 'true') {
    return <Navigate to="/onboarding" replace />;
  }

  // 6. Tiered routing for students
  if (isKids) {
    return <Navigate to="/kids" replace />;
  }
  if (isTeen) {
    return <Navigate to="/teen" replace />;
  }
  return <Navigate to="/app" replace />;
}

function ViedmaRedirect() {
  const { isKids, isTeen } = useStudentTier();
  const { user } = useAuth();
  const role = user?.role?.toLowerCase();
  const isAdmin = role === 'admin' || role === 'superadmin' || user?.is_superuser;

  let portalKey = 'viedma';
  let target = '/app/viedma';

  if (!isAdmin) {
    if (isKids) {
      portalKey = 'viedma_kids';
      target = '/kids/viedma';
    } else if (isTeen) {
      portalKey = 'viedma_teen';
      target = '/teen/viedma';
    }
  }

  if (typeof sessionStorage !== 'undefined') {
    sessionStorage.setItem('last_dashboard_portal', portalKey);
  }
  if (typeof localStorage !== 'undefined') {
    localStorage.setItem('last_dashboard_portal', portalKey);
  }
  return <Navigate to={target} replace />;
}

export default function AppRouter() {
  const { isAuthenticated, isVerified, isImpersonating, user } = useAuth();
  const navigate = useNavigate();
  const canAccessApp = isVerified || isImpersonating;

  useEffect(() => {
    if (isAuthenticated && canAccessApp && (user?.role?.toLowerCase() === 'student' || user?.role?.toLowerCase() === 'guest')) {
      const pendingToken = localStorage.getItem('pending_invite_token');
      if (pendingToken) {
        localStorage.removeItem('pending_invite_token');
        navigate(`/app/invite?token=${encodeURIComponent(pendingToken)}`, { replace: true });
      }
    }
  }, [canAccessApp, isAuthenticated, user, navigate]);

  return (
    <Routes>

      {/* School dashboard — requires GET /api/v1/users/me/school (linked school), not role alone */}
      <Route
        path="/school/*"
        element={
          isAuthenticated ? (
            canAccessApp ? (
              <LazyRoute component={SchoolRoute} />
            ) : (
              <Navigate to="/verify" replace />
            )
          ) : (
            <Navigate to="/login" replace />
          )
        }
      />

      {/* 1. Admin routes - strictly protected for admins & superadmins */}
      <Route
        path="/admin/*"
        element={
          isAuthenticated ? (
            canAccessApp ? (
              (user?.role?.toLowerCase() === 'admin' || user?.is_superuser) ? (
                <LazyRoute component={AdminRoute} />
              ) : (
                <Navigate to="/app" replace />
              )
            ) : (
              <Navigate to="/verify" replace />
            )
          ) : (
            <Navigate to="/login" replace />
          )
        }
      />

      {/* 2. Public family invitation acceptance route & onboarding */}
      <Route
        path="/onboarding"
        element={
          <GuestLayout>
            <LazyRoute component={Onboarding} />
          </GuestLayout>
        }
      />
      <Route path="/family/invite" element={<LazyRoute component={FamilyInvitationAccept} />} />
      <Route path="/app/invite" element={<LazyRoute component={FamilyInvitationAccept} />} />
      <Route path="/app/school-invite" element={<LazyRoute component={SchoolInviteLanding} />} />
      <Route path="/app/school-invite/success" element={<LazyRoute component={SchoolInviteSuccess} />} />
      <Route path="/circuit-builder" element={<LazyRoute component={CircuitLessonPage} />} />

      {/* Dedicated Teen portal routes */}
      <Route
        path="/teen/*"
        element={
          isAuthenticated ? (
            canAccessApp ? (
              <LazyRoute component={TeenRouter} />
              
            ) : (
              <Navigate to="/verify" replace />
            )
          ) : (
            <Navigate to="/login" replace />
          )
        }
      />
      <Route
        path="/teen"
        element={
          isAuthenticated ? (
            canAccessApp ? (
              <LazyRoute component={TeenRouter} />
              
            ) : (
              <Navigate to="/verify" replace />
            )
          ) : (
            <Navigate to="/login" replace />
          )
        }
      />
      <Route path="/teens/library" element={<Navigate to="/teen/library" replace />} />
      <Route path="/teens/assignments" element={<Navigate to="/teen/assignments" replace />} />
      <Route path="/teens/tasks" element={<Navigate to="/teen/assignments" replace />} />
      <Route path="/teens/*" element={<Navigate to="/teen" replace />} />
      <Route path="/teens" element={<Navigate to="/teen" replace />} />

      {/* 3. Dedicated Kids portal routes */}
      <Route
        path="/kids/*"
        element={
          isAuthenticated ? (
            canAccessApp ? (
              <LazyRoute component={KidsRouter} />
            ) : (
              <Navigate to="/verify" replace />
            )
          ) : (
            <Navigate to="/login" replace />
          )
        }
      />

      {/* Dedicated Quran & Islamic reading portal routes */}
      <Route
        path="/quran"
        element={
          isAuthenticated ? (
            canAccessApp ? (
              <LazyRoute component={QuranRouter} />
            ) : (
              <Navigate to="/verify" replace />
            )
          ) : (
            <Navigate to="/login?portal=quran&redirect=/quran" replace />
          )
        }
      />
      <Route
        path="/quran/*"
        element={
          isAuthenticated ? (
            canAccessApp ? (
              <LazyRoute component={QuranRouter} />
            ) : (
              <Navigate to="/verify" replace />
            )
          ) : (
            <Navigate to="/login?portal=quran&redirect=/quran" replace />
          )
        }
      />

      {/* Top-level Viedma portal route alias */}
      <Route
        path="/viedma"
        element={
          isAuthenticated ? (
            canAccessApp ? (
              <ViedmaRedirect />
            ) : (
              <Navigate to="/verify" replace />
            )
          ) : (
            <Navigate to="/login?portal=viedma&redirect=/app/viedma" replace />
          )
        }
      />
      <Route
        path="/viedma/*"
        element={
          isAuthenticated ? (
            canAccessApp ? (
              <ViedmaRedirect />
            ) : (
              <Navigate to="/verify" replace />
            )
          ) : (
            <Navigate to="/login?portal=viedma&redirect=/app/viedma" replace />
          )
        }
      />

      {/* 4. Protected app routes */}
      {/* Move AuthRouter first so /app/something is matched correctly */}
      <Route
        path="/app/*"
        element={
          isAuthenticated ? (
            canAccessApp ? (
              <AdminAppRedirect>
                <AppSchoolRedirect>
                  <LazyRoute component={AuthRouter} />
                </AppSchoolRedirect>
              </AdminAppRedirect>
            ) : (
              <Navigate to="/verify" replace />
            )
          ) : (
            <Navigate to="/login" replace />
          )
        }
      />

      {/* 5. Root landing route */}
      <Route
        path="/"
        element={
          isAuthenticated ? (
            canAccessApp ? (
              <AppSchoolRedirect>
                <StudentAppRedirect />
              </AppSchoolRedirect>
            ) : (
              <Navigate to="/verify" replace />
            )
          ) : (
            <LazyRoute component={GuestRouter} />
          )
        }
      />

      {/* 6. Guest & Verification routes */}
      <Route
        path="/login"
        element={
          isAuthenticated && canAccessApp ? (
            <AppSchoolRedirect>
              <StudentAppRedirect />
            </AppSchoolRedirect>
          ) : (
            <LazyRoute component={GuestRouter} />
          )
        }
      />
      <Route
        path="/register"
        element={
          isAuthenticated && canAccessApp ? (
            <AppSchoolRedirect>
              <StudentAppRedirect />
            </AppSchoolRedirect>
          ) : (
            <LazyRoute component={GuestRouter} />
          )
        }
      />
      <Route
        path="/verify"
        element={<LazyRoute component={GuestRouter} />}
      />
      <Route
        path="/forgot-password"
        element={
          isAuthenticated && canAccessApp ? (
            <AppSchoolRedirect>
              <StudentAppRedirect />
            </AppSchoolRedirect>
          ) : (
            <LazyRoute component={GuestRouter} />
          )
        }
      />
      <Route
        path="/reset-password"
        element={<LazyRoute component={GuestRouter} />}
      />

      {/* Shorthand language aliases */}
      <Route path="/login=:lang" element={<LazyRoute component={GuestRouter} />} />
      <Route path="/login/:lang" element={<LazyRoute component={GuestRouter} />} />
      <Route path="/register=:lang" element={<LazyRoute component={GuestRouter} />} />
      <Route path="/register/:lang" element={<LazyRoute component={GuestRouter} />} />
      <Route path="/verify=:lang" element={<LazyRoute component={GuestRouter} />} />
      <Route path="/verify/:lang" element={<LazyRoute component={GuestRouter} />} />
      <Route path="/forgot-password=:lang" element={<LazyRoute component={GuestRouter} />} />
      <Route path="/forgot-password/:lang" element={<LazyRoute component={GuestRouter} />} />

      {/* Global 404 Catch-All */}
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}
